import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, MenuItem, Typography, Tooltip, Snackbar, Alert } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleFilledIcon from '@mui/icons-material/CheckCircle';
import CancelFilledIcon from '@mui/icons-material/Cancel';
import { useInventoryRequests } from './InventoryRequests.hooks';
import { useInventoryRequestPermissions } from '../../../hooks/useInventoryRequestPermissions';
import { useCanChangeInventoryLocation } from '../../../hooks/useCanChangeInventoryLocation';
import { canResolveInventoryRequest } from '../../../utils/inventory/canResolveInventoryRequest';
import { selectCurrentUser } from '../../../redux/slices/auth.slice';
import { BaseTable, TableColumn } from '../../common/Table';
import { StatusChip as StatusChipCommon, ActionButton } from '../../common/Table/Table.styles';
import { mapRequestStatus } from '../../common/status';
import EmptyStateMessage from '../../common/EmptyStateMessage';
import CommonPagination from '../../common/Pagination';
import {
  ListPageFiltersSection,
  ListPageFilterContainer,
  ListPagePrimaryFilterRow,
  ListPageFilterCollapse,
  ListPageEqualSecondaryFilterRow,
  ListPageFilterTextField,
  ListPageActiveFilters,
  type ListPageActiveFilterChip,
} from '../../common/list-page';
import { StatusSummaryBar, type StatusSummaryMetric } from '../../common/StatusSummaryBar';
import RequestDetailsModal from './RequestDetailsModal';
import { LoadingSpinner } from '../InventoryTransfer/InventoryTransfer.styles';
import { useNavigationStateOnce } from '../../../hooks/useNavigationStateOnce';
import { HOME_PENDING_STATUS_FILTER } from '../../../constants/homeNavigation.constants';
import { STATUS_COLUMN_SORTABLE } from '../../../constants/listSort.constants';

const InventoryRequests: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialHomeStatus = (location.state as { filterRequestStatus?: string } | null)
    ?.filterRequestStatus;
  const requestPerms = useInventoryRequestPermissions();
  const canAccessAllLocations = useCanChangeInventoryLocation();
  const currentUser = useSelector(selectCurrentUser);

  const {
    requests,
    locations,
    filters,
    appliedFilters,
    pagination,
    isLoading,
    isSearching,
    showMoreFilters,
    showDetailsModal,
    selectedRequestDetail,
    isLoadingDetails,
    notification,
    statusSummary,
    statusSummaryLoading,
    handleFilterChange,
    handleSelectChange,
    handleRequestCodeKeyPress,
    handleResetFilters,
    handleSearch,
    clearAppliedFilterField,
    applyDeepLinkFilter,
    applyStatusFilter,
    applyHomeStatusFilter,
    requestSort,
    handlePageChange,
    handleItemsPerPageChange,
    toggleMoreFilters,
    handleCloseModal,
    handleViewDetails,
    handleCloseNotification,
    refreshAfterMutation,
    setNotification,
  } = useInventoryRequests(
    // Desde Inicio ("Solicitudes pendientes"): la primera carga ya sale filtrada.
    initialHomeStatus ?? '',
  );
  const claimHomeNavigation = useNavigationStateOnce(
    initialHomeStatus != null && initialHomeStatus !== '',
  );

  const REQUEST_SUMMARY_METRICS: StatusSummaryMetric[] = useMemo(
    () => [
      {
        key: 'total',
        label: 'Total solicitudes',
        icon: <SummarizeIcon />,
        iconVariant: 'inventory',
        filterValue: '',
      },
      {
        key: 'pending',
        label: 'Pendientes',
        icon: <AccessTimeIcon />,
        iconVariant: 'warning',
        filterValue: HOME_PENDING_STATUS_FILTER.requests,
      },
      {
        key: 'approved',
        label: 'Aprobadas',
        icon: <CheckCircleFilledIcon />,
        iconVariant: 'sales',
        filterValue: 'Aprobada',
      },
      {
        key: 'rejected',
        label: 'Rechazadas',
        icon: <CancelFilledIcon />,
        iconVariant: 'cost',
        filterValue: 'Rechazada',
      },
    ],
    [],
  );

  const [detailsModalMode, setDetailsModalMode] = useState<'view' | 'resolve'>('view');

  const resolveContext = useMemo(
    () => ({
      canApprove: requestPerms.canApprove,
      canAccessAllLocations,
      userPrimaryLocationId: currentUser?.primaryLocationId,
    }),
    [canAccessAllLocations, currentUser?.primaryLocationId, requestPerms.canApprove],
  );

  const canResolveRequest = useCallback(
    (row: (typeof requests)[number]) =>
      canResolveInventoryRequest({
        status: row.status,
        sourceLocationId: row.sourceLocationId,
        ...resolveContext,
      }),
    [resolveContext],
  );

  const canResolveSelected = useMemo(() => {
    if (!selectedRequestDetail) {
      return false;
    }
    return canResolveInventoryRequest({
      status: selectedRequestDetail.status,
      sourceLocationId: selectedRequestDetail.sourceLocationId,
      ...resolveContext,
    });
  }, [resolveContext, selectedRequestDetail]);

  const handleOpenDetails = useCallback(
    (requestId: string, mode: 'view' | 'resolve') => {
      setDetailsModalMode(mode);
      void handleViewDetails(requestId);
    },
    [handleViewDetails],
  );

  const handleCloseDetailsModal = useCallback(() => {
    handleCloseModal();
    setDetailsModalMode('view');
  }, [handleCloseModal]);

  // Deep-link: filtrar por Código SOL / estado desde Inicio; abrir detalle
  useEffect(() => {
    const state = location.state as {
      openRequestDetails?: number;
      filterRequestCode?: string;
      filterRequestStatus?: string;
    } | null;
    if (
      state?.openRequestDetails == null &&
      !state?.filterRequestCode &&
      !state?.filterRequestStatus
    ) {
      return;
    }

    // Clic en la tarjeta de Inicio con la pestaña ya abierta: vista limpia con solo ese estado.
    // En pestaña nueva ya vino como estado inicial del hook.
    if (state.filterRequestStatus && claimHomeNavigation(location.key)) {
      applyHomeStatusFilter(state.filterRequestStatus);
    }
    if (state.filterRequestCode) {
      applyDeepLinkFilter(state.filterRequestCode);
    }
    if (state.openRequestDetails != null) {
      handleOpenDetails(String(state.openRequestDetails), 'view');
    }

    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: {} },
    );
  }, [
    location.state,
    claimHomeNavigation,
    location.key,
    location.pathname,
    location.search,
    handleOpenDetails,
    applyDeepLinkFilter,
    applyHomeStatusFilter,
    navigate,
  ]);

  const handleNotify = useCallback(
    (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
      setNotification({ open: true, message, severity });
    },
    [setNotification],
  );

  const handleOpenLinkedTransfer = useCallback(
    (transferId: number, transferCode?: string) => {
      navigate('/dashboard/inventory/transfer', {
        state: {
          openTransferDetails: transferId,
          ...(transferCode ? { filterTransferCode: transferCode } : {}),
        },
      });
    },
    [navigate],
  );

  const columns: TableColumn<(typeof requests)[number]>[] = useMemo(
    () => [
      {
        id: 'requestCode',
        label: 'Código SOL',
        sortField: 'requestCode',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={600} fontSize="0.95rem">
            {item.requestCode}
          </Typography>
        ),
      },
      {
        id: 'destination',
        label: 'Solicitud Origen',
        sortField: 'destinationLocationName',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.destination}
          </Typography>
        ),
      },
      {
        id: 'source',
        label: 'Solicitud Destino',
        sortField: 'sourceLocationName',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.source}
          </Typography>
        ),
      },
      {
        id: 'createdBy',
        label: 'Solicitado por',
        sortField: 'createdByName',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.createdBy ?? '—'}
          </Typography>
        ),
      },
      {
        id: 'date',
        label: 'Fecha',
        sortField: 'createdAt',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.date}
          </Typography>
        ),
      },
      {
        id: 'itemCount',
        label: 'Piezas',
        sortField: 'itemCount',
        align: 'center',
        render: (item) => (
          <Typography color="text.primary" fontWeight={600} fontSize="0.95rem">
            {item.itemCount}
          </Typography>
        ),
      },
      {
        id: 'status',
        label: 'Estado',
        sortField: 'status',
        sortable: STATUS_COLUMN_SORTABLE,
        render: (item) => {
          const chip = mapRequestStatus(item.statusLabel);
          return <StatusChipCommon label={chip.label} size="small" color={chip.color} />;
        },
      },
    ],
    [],
  );

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const locName = (id: string) => locations.find((l) => l.id.toString() === id)?.name || id;

    if (appliedFilters.requestCode) {
      chips.push({
        key: 'requestCode',
        label: `Código: ${appliedFilters.requestCode}`,
        onRemove: () => clearAppliedFilterField('requestCode'),
      });
    }
    if (appliedFilters.destinationLocation) {
      chips.push({
        key: 'destinationLocation',
        label: `Solicitud Origen: ${locName(appliedFilters.destinationLocation)}`,
        onRemove: () => clearAppliedFilterField('destinationLocation'),
      });
    }
    if (appliedFilters.sourceLocation) {
      chips.push({
        key: 'sourceLocation',
        label: `Solicitud Destino: ${locName(appliedFilters.sourceLocation)}`,
        onRemove: () => clearAppliedFilterField('sourceLocation'),
      });
    }
    if (appliedFilters.status) {
      chips.push({
        key: 'status',
        label: `Estado: ${mapRequestStatus(appliedFilters.status).label}`,
        onRemove: () => clearAppliedFilterField('status'),
      });
    }
    if (appliedFilters.dateFrom) {
      chips.push({
        key: 'dateFrom',
        label: `Desde: ${appliedFilters.dateFrom}`,
        onRemove: () => clearAppliedFilterField('dateFrom'),
      });
    }
    if (appliedFilters.dateTo) {
      chips.push({
        key: 'dateTo',
        label: `Hasta: ${appliedFilters.dateTo}`,
        onRemove: () => clearAppliedFilterField('dateTo'),
      });
    }
    return chips;
  }, [appliedFilters, locations, clearAppliedFilterField]);

  if (!requestPerms.canView) {
    return (
      <EmptyStateMessage
        message="No tiene permiso para ver solicitudes de inventario."
        minHeight={200}
      />
    );
  }

  return (
    <>
      <StatusSummaryBar
        metrics={REQUEST_SUMMARY_METRICS}
        counts={{
          total: statusSummary.total,
          pending: statusSummary.pending,
          approved: statusSummary.approved,
          rejected: statusSummary.rejected,
        }}
        loading={statusSummaryLoading}
        activeFilterValue={appliedFilters.status}
        onMetricClick={applyStatusFilter}
      />

      <ListPageFiltersSection>
        <ListPageFilterContainer>
          <ListPagePrimaryFilterRow
            showMoreFilters={showMoreFilters}
            onToggleMoreFilters={toggleMoreFilters}
            onReset={handleResetFilters}
            onSearch={handleSearch}
            searchFieldProps={{
              label: 'Código SOL',
              name: 'requestCode',
              value: filters.requestCode,
              onChange: handleFilterChange,
              onKeyDown: handleRequestCodeKeyPress,
            }}
          />

          <ListPageFilterCollapse in={showMoreFilters} timeout={350} unmountOnExit>
            <ListPageEqualSecondaryFilterRow>
              <ListPageFilterTextField
                fullWidth
                select
                variant="outlined"
                size="medium"
                label="Solicitud Origen"
                name="destinationLocation"
                value={filters.destinationLocation}
                onChange={handleSelectChange}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id.toString()}>
                    {location.name}
                  </MenuItem>
                ))}
              </ListPageFilterTextField>

              <ListPageFilterTextField
                fullWidth
                select
                variant="outlined"
                size="medium"
                label="Solicitud Destino"
                name="sourceLocation"
                value={filters.sourceLocation}
                onChange={handleSelectChange}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id.toString()}>
                    {location.name}
                  </MenuItem>
                ))}
              </ListPageFilterTextField>

              <ListPageFilterTextField
                fullWidth
                select
                variant="outlined"
                size="medium"
                label="Estado"
                name="status"
                value={filters.status}
                onChange={handleSelectChange}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                <MenuItem value="Pendiente">Pendiente</MenuItem>
                <MenuItem value="Aprobada">Aprobada</MenuItem>
                <MenuItem value="Rechazada">Rechazada</MenuItem>
              </ListPageFilterTextField>

              <ListPageFilterTextField
                fullWidth
                variant="outlined"
                size="medium"
                label="Fecha Desde"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />

              <ListPageFilterTextField
                fullWidth
                variant="outlined"
                size="medium"
                label="Fecha Hasta"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </ListPageEqualSecondaryFilterRow>
          </ListPageFilterCollapse>

          <ListPageActiveFilters chips={activeFilterChips} />
        </ListPageFilterContainer>
      </ListPageFiltersSection>

      {!isLoading && requests.length > 0 && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <BaseTable
            items={requests}
            columns={columns}
            selectedItems={[]}
            onSelectItems={() => {}}
            showCheckbox={false}
            showActions
            getItemId={(item) => item.id}
            emptyStateTitle="No se encontraron solicitudes"
            sortBy={requestSort.sortBy}
            sortOrder={requestSort.sortOrder}
            onRequestSort={requestSort.requestSort}
            renderActions={(row) => (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Tooltip title="Ver detalles">
                  <ActionButton size="small" onClick={() => handleOpenDetails(row.id, 'view')}>
                    <VisibilityIcon fontSize="small" />
                  </ActionButton>
                </Tooltip>
                {canResolveRequest(row) && (
                  <Tooltip title="Resolver solicitud">
                    <ActionButton size="small" onClick={() => handleOpenDetails(row.id, 'resolve')}>
                      <CheckCircleIcon fontSize="small" />
                    </ActionButton>
                  </Tooltip>
                )}
              </Box>
            )}
          />
        </Box>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <LoadingSpinner />
        </Box>
      )}

      {isSearching && !isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <LoadingSpinner />
        </Box>
      )}

      {!isLoading && !isSearching && requests.length === 0 && (
        <EmptyStateMessage message="No se encontraron solicitudes de inventario" minHeight={200} />
      )}

      {!isLoading && !isSearching && pagination.total > 0 && (
        <Box>
          <CommonPagination
            currentPage={pagination.currentPage}
            itemsPerPage={pagination.itemsPerPage}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </Box>
      )}

      <RequestDetailsModal
        open={showDetailsModal}
        mode={detailsModalMode}
        isLoadingDetails={isLoadingDetails}
        requestDetail={selectedRequestDetail}
        canResolve={canResolveSelected}
        onClose={handleCloseDetailsModal}
        onResolved={refreshAfterMutation}
        onNotify={handleNotify}
        onOpenLinkedTransfer={handleOpenLinkedTransfer}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InventoryRequests;
